import { css } from '@emotion/react';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Fieldset } from '@spark-web/fieldset';
import { Radio, RadioGroup } from '@spark-web/radio';
import { Stack } from '@spark-web/stack';
import { useTheme } from '@spark-web/theme';
import { FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Alert } from '~/components/common/alert';
import { SortIcon } from '~/components/common/icon';
import { PARTNER_WITH_PRICING_DISPLAY } from '~/constants';
import { useModal } from '~/hooks/useModal';
import { MarketplacePartner, MarketplaceSort } from '~/types/marketplace';

import { Button as BrighteButton } from '../../common/button';

import { FilterSortBaseDialog } from './filter-sort-base-dialog';

export type SortFormData = {
  sortType: MarketplaceSort;
};

export type SortDialogProps = {
  initialValue?: MarketplaceSort | null;
  defaultSort?: MarketplaceSort;
  partner?: MarketplacePartner;
  status?: string;
  onSort?: (sortType: MarketplaceSort) => void;
};

export const SortDialog: FC<SortDialogProps> = ({ initialValue, defaultSort, partner, status, onSort }) => {
  const theme = useTheme();

  const { isOpen, open, close, ref: triggerRef } = useModal('sort-vendors');

  const { control, handleSubmit } = useForm<SortFormData>({
    defaultValues: {
      sortType: initialValue ?? defaultSort ?? 'RANKING',
    },
  });

  const isPricingVisible = partner ? PARTNER_WITH_PRICING_DISPLAY.includes(partner) : false;
  const sortOptions = useMemo(() => {
    const options = [{ label: 'Distance', value: 'DISTANCE' }];

    if (isPricingVisible) {
      options.push(
        { label: 'Price low to high', value: 'PRICE_LOW_TO_HIGH' },
        { label: 'Price high to low', value: 'PRICE_HIGH_TO_LOW' },
      );
    }

    // Only add DEFAULT option if the channel sort is defined and not DISTANCE
    if (defaultSort && defaultSort !== 'DISTANCE') {
      options.unshift({ label: 'Default', value: defaultSort });
    }

    return options;
  }, [isPricingVisible, defaultSort]);

  const onDialogCloseAutoFocus = (event: Event) => {
    event.preventDefault();
    triggerRef.current?.focus();
  };

  const onSubmit = (data: SortFormData) => {
    onSort?.(data.sortType);
    close();
  };

  return (
    <>
      <Button
        data={{ testId: 'sort-button' }}
        tone="neutral"
        onClick={open}
        rounded
        size="large"
        ref={triggerRef}
        css={css({
          height: 48,
          width: '100%',
          alignSelf: 'center',
          [`@media (min-width: ${theme.breakpoint.tablet}px)`]: {
            width: 156,
          },
        })}
      >
        Sort <SortIcon partner={partner} brighteSize="xsmall" />
      </Button>
      <FilterSortBaseDialog
        isOpen={isOpen}
        onToggle={close}
        onCloseAutoFocus={onDialogCloseAutoFocus}
        title="Sort results by"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack gap="xxlarge">
          <Fieldset gap="large">
            <Controller
              name="sortType"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RadioGroup aria-label="sort by" value={value} onChange={onChange}>
                  {sortOptions.map((option) => (
                    <Stack key={option.value} gap="large">
                      <Stack paddingX="small">
                        <Radio value={option.value}>{option.label}</Radio>
                      </Stack>
                      <Divider />
                    </Stack>
                  ))}
                </RadioGroup>
              )}
            />
          </Fieldset>
        </Stack>
        <Stack>
          {status === 'error' ? <Alert error="GENERIC_ERROR" partner={partner as MarketplacePartner} /> : null}
          <Stack flex={1}>
            <BrighteButton loading={status === 'loading'} rounded type="submit">
              Apply
            </BrighteButton>
          </Stack>
        </Stack>
      </FilterSortBaseDialog>
    </>
  );
};
